import {storeToRefs} from 'pinia';
import {defineNuxtRouteMiddleware, navigateTo, useNuxtApp} from '#app';
import {useAuth} from '#imports';
import {safelyNavigateTo} from '@malt/utils';
import type {StepsToPagesMapper} from '~/lib/steps';
import {PAGES, stepsToPagesMapper} from '~/lib/steps';
import {useSignupStore} from '~/store/signup.store';

export default defineNuxtRouteMiddleware(async (to, from) => {
    // Composables
    const nuxtApp = useNuxtApp();

    // Stores
    const {$pinia} = nuxtApp;
    const signupStore = useSignupStore($pinia);
    const {getSignupState, setRedirectedOnEmailValidation, resetRedirectPath} = signupStore;
    const {redirectPath} = storeToRefs(signupStore);
    const {loggedIn} = useAuth();

    // dev only
    if (import.meta.dev && ['/dev-home', '/signin'].includes(to.path) && to.path !== '/') {
        return;
    }

    if (['/welcome-to-malt'].includes(to.path)) {
        if (!loggedIn) {
            return await navigateTo('/who-are-you', {external: true});
        }

        return;
    }

    if (['/email-verification'].includes(to.path)) {
        return;
    }

    if (to.path.startsWith('/invitation') || to.path.startsWith('/missing-company-details/') || to.path.startsWith('/admin/')) {
        return;
    }

    // Disable redirection when going:
    // - from /email to /password
    // - from /email-validation to /email
    // - from /public-email-warning to /email
    if (
        (from.path === PAGES.EMAIL && to.path === PAGES.PASSWORD) ||
        (from.path === PAGES.EMAIL_VALIDATION && to.path === PAGES.EMAIL) ||
        (from.path === PAGES.PUBLIC_EMAIL_WARNING && to.path === PAGES.EMAIL)
    ) {
        return;
    }

    const signupState = await getSignupState();

    const redirectPathVal = redirectPath.value;
    if (redirectPathVal) {
        resetRedirectPath();
        return safelyNavigateTo(redirectPathVal, {externalToThisApp: true});
    }

    const nextStep = stepsToPagesMapper[signupState.nextStep?.name as keyof StepsToPagesMapper];

    // The user reloaded the page or dropped off from the workflow and went back
    if (import.meta.server && nextStep === PAGES.EMAIL_VALIDATION) {
        setRedirectedOnEmailValidation();
    }

    // Client signup is completed = user is logged in
    if (!nextStep && signupState.status === 'COMPLETED') {
        if (to.query && typeof to.query.redirect === 'string') {
            return safelyNavigateTo(to.query.redirect, {externalToThisApp: true});
        }

        return navigateTo('/welcome-to-malt');
    }

    // Avoid infinite redirection loop
    if (to.path !== nextStep) {
        return navigateTo({
            path: nextStep,
            query: to.query,
        });
    }
});
